import { useSyncedDraft } from '@/lib/useSyncedDraft';

import { type ClaimableField } from './useClaimableField';

/**
 * A synced draft on a claimable field, with the focus/blur wiring fused:
 * focusing protects the draft and announces the focus claim; blurring
 * releases the claim, then runs the field's commit with the final draft
 * (`setDraft` is passed alongside so a commit can reset a cleared field).
 * Wire the returned `onFocus`/`onBlur` straight into the input. Takes the
 * field rather than calling `useClaimableField` itself so the caller keeps
 * a hook-direct reference (the React compiler tracks refs through it).
 */
export function useClaimableDraft<T>(
  field: ClaimableField,
  synced: T,
  commitOnBlur: (draft: T, setDraft: (value: T) => void) => void,
): {
  draft: T;
  setDraft: (value: T) => void;
  onFocus: () => void;
  onBlur: () => void;
} {
  const { draft, setDraft, onFocus, onBlur } = useSyncedDraft(synced);
  return {
    draft,
    setDraft,
    onFocus: () => {
      onFocus();
      field.focused();
    },
    onBlur: () => {
      onBlur();
      field.blurred();
      commitOnBlur(draft, setDraft);
    },
  };
}
